export type PapeleraRole = "admin" | "reseller";

export interface PapeleraColumn {
  key: string;
  labelKey: string;
}

export interface PapeleraEntity {
  id: string;
  labelKey: string;
  icon: string;
  hardDeleteIdParam: "id" | "remolque_id" | "trip_id" | "ruta_id";
  columns: PapeleraColumn[];
  // Roles que pueden GESTIONAR (ver / restaurar / borrar) esta entidad.
  roles: PapeleraRole[];
  // Si true, la entidad NO expone "Borrar definitivo" (solo Restaurar).
  // Ej: companies — la purga total queda como deuda (ver SPEC §11.7).
  noHardDelete?: boolean;
  // Por defecto true. Si false, la entidad no se filtra por la compañía
  // seleccionada (p.ej. companies, que no tienen company_id) y sus acciones no
  // requieren tener una compañía seleccionada en el header.
  companyScoped?: boolean;
  // Si se define, la entidad SOLO es visible/gestionable cuando la compañía
  // activa (seleccionada en el header) está en esta lista. Ej: patios de
  // maniobras, habilitados solo para una compañía piloto.
  companyAllowList?: string[];
}

const ADMIN_AND_RESELLER: PapeleraRole[] = ["admin", "reseller"];
const RESELLER_ONLY: PapeleraRole[] = ["reseller"];

export const PAPELERA_ENTITIES: PapeleraEntity[] = [
  {
    id: "device",
    labelKey: "papelera.entities.device",
    icon: "ri-smartphone-line",
    hardDeleteIdParam: "id",
    columns: [
      { key: "identifier", labelKey: "papelera.columns.identifier" },
      { key: "name", labelKey: "papelera.columns.name" },
    ],
    roles: RESELLER_ONLY,
  },
  {
    id: "vehicle",
    labelKey: "papelera.entities.vehicle",
    icon: "ri-truck-line",
    hardDeleteIdParam: "id",
    columns: [
      { key: "plate", labelKey: "papelera.columns.plate" },
      { key: "name", labelKey: "papelera.columns.name" },
    ],
    roles: RESELLER_ONLY,
  },
  {
    id: "remolque",
    labelKey: "papelera.entities.remolque",
    icon: "ri-box-3-line",
    hardDeleteIdParam: "remolque_id",
    columns: [
      { key: "plate", labelKey: "papelera.columns.plate" },
      { key: "name", labelKey: "papelera.columns.name" },
    ],
    roles: RESELLER_ONLY,
  },
  {
    id: "user",
    labelKey: "papelera.entities.user",
    icon: "ri-user-settings-line",
    hardDeleteIdParam: "id",
    columns: [
      { key: "email", labelKey: "papelera.columns.email" },
      { key: "username", labelKey: "papelera.columns.username" },
    ],
    roles: ADMIN_AND_RESELLER,
  },
  {
    id: "driver",
    labelKey: "papelera.entities.driver",
    icon: "ri-steering-2-line",
    hardDeleteIdParam: "id",
    columns: [
      { key: "name", labelKey: "papelera.columns.name" },
      { key: "phone", labelKey: "papelera.columns.phone" },
    ],
    roles: ADMIN_AND_RESELLER,
  },
  {
    id: "geofence",
    labelKey: "papelera.entities.geofence",
    icon: "ri-map-pin-range-line",
    hardDeleteIdParam: "id",
    columns: [
      { key: "name", labelKey: "papelera.columns.name" },
      { key: "description", labelKey: "papelera.columns.description" },
    ],
    roles: ADMIN_AND_RESELLER,
  },
  {
    id: "trip",
    labelKey: "papelera.entities.trip",
    icon: "ri-road-map-line",
    hardDeleteIdParam: "trip_id",
    columns: [
      { key: "name", labelKey: "papelera.columns.name" },
      { key: "origin", labelKey: "papelera.columns.origin" },
    ],
    roles: ADMIN_AND_RESELLER,
  },
  {
    id: "ruta",
    labelKey: "papelera.entities.ruta",
    icon: "ri-route-line",
    hardDeleteIdParam: "ruta_id",
    columns: [
      { key: "name", labelKey: "papelera.columns.name" },
      { key: "description", labelKey: "papelera.columns.description" },
    ],
    roles: ADMIN_AND_RESELLER,
  },
  // {
  //   id: "chat",
  //   labelKey: "papelera.entities.chat",
  //   icon: "ri-chat-3-line",
  //   hardDeleteIdParam: "id",
  //   columns: [
  //     { key: "title", labelKey: "papelera.columns.title" },
  //     { key: "name", labelKey: "papelera.columns.name" },
  //   ],
  //   roles: ADMIN_AND_RESELLER,
  // },
  {
    id: "group",
    labelKey: "papelera.entities.group",
    icon: "ri-group-line",
    hardDeleteIdParam: "id",
    columns: [
      { key: "name", labelKey: "papelera.columns.name" },
      { key: "description", labelKey: "papelera.columns.description" },
    ],
    roles: ADMIN_AND_RESELLER,
  },
  {
    id: "pointInterest",
    labelKey: "papelera.entities.pointInterest",
    icon: "ri-map-pin-line",
    hardDeleteIdParam: "id",
    columns: [
      { key: "name", labelKey: "papelera.columns.name" },
      { key: "description", labelKey: "papelera.columns.description" },
    ],
    roles: ADMIN_AND_RESELLER,
  },
  {
    id: "aviso",
    labelKey: "papelera.entities.aviso",
    icon: "ri-notification-3-line",
    hardDeleteIdParam: "id",
    columns: [
      { key: "title", labelKey: "papelera.columns.title" },
      { key: "description", labelKey: "papelera.columns.description" },
    ],
    roles: ADMIN_AND_RESELLER,
  },
  {
    id: "trackingLink",
    labelKey: "papelera.entities.trackingLink",
    icon: "ri-links-line",
    hardDeleteIdParam: "id",
    columns: [
      { key: "name", labelKey: "papelera.columns.name" },
      { key: "description", labelKey: "papelera.columns.description" },
    ],
    roles: ADMIN_AND_RESELLER,
  },
  {
    id: "trackingReplay",
    labelKey: "papelera.entities.trackingReplay",
    icon: "ri-film-line",
    hardDeleteIdParam: "id",
    columns: [
      { key: "name", labelKey: "papelera.columns.name" },
      { key: "description", labelKey: "papelera.columns.description" },
    ],
    roles: ADMIN_AND_RESELLER,
  },
  {
    id: "yard",
    labelKey: "papelera.entities.yard",
    icon: "ri-building-4-line",
    hardDeleteIdParam: "id",
    columns: [
      { key: "name", labelKey: "papelera.columns.name" },
      { key: "location", labelKey: "papelera.columns.location" },
    ],
    roles: ADMIN_AND_RESELLER,
    // Piloto: solo visible para la compañía habilitada.
    companyAllowList: ["zQ4Xpa88F7RstUvLKc5"],
  },
  {
    id: "company",
    labelKey: "papelera.entities.company",
    icon: "ri-building-line",
    hardDeleteIdParam: "id",
    columns: [
      { key: "name", labelKey: "papelera.columns.name" },
      { key: "business_name", labelKey: "papelera.columns.businessName" },
    ],
    roles: RESELLER_ONLY,
    companyScoped: false,
  },
];

export const getPapeleraEntity = (id?: string): PapeleraEntity | undefined =>
  PAPELERA_ENTITIES.find((e) => e.id === id);
